/**
 * PlanillaInscripcionModal
 * ------------------------
 * Deja que el director se baje la PLANILLA GENERAL DE INSCRIPCIÓN de su club
 * — la misma del formato en papel de la Asociación — en Excel o en PDF.
 *
 * Todo lo que la planilla pide ya está inscrito: nombres, documentos, edades,
 * EPS, cargos y hasta si el aventurero es bautizado (venía separado en dos
 * categorías de precio). Sólo quedaban tres rótulos del encabezado que la
 * plataforma no modela en ninguna parte — zona, pastor asistente y ministro —
 * así que se piden aquí y se guardan en el club para la próxima vez.
 *
 * El otro dato que falta es el uniforme, que separa a los aventureros
 * uniformados de los "amigos". Ese se marca en la tabla "Mis Inscritos", y si
 * no hay ninguno marcado el modal lo avisa antes de generar: una planilla con
 * los 25 niños en la sección de amigos es un error que no se nota hasta que
 * ya está impresa.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { AlertTriangle, Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { Modal, ModalFooter, Button, Input } from "@/components/ui";
import { churchService, clubService } from "@/lib/services/organization";
import { personService } from "@/lib/services/persons";
import { buildPersonsMap } from "@/lib/utils/eventExport";
import {
  buildPlanilla,
  isParticipantCategory,
  isPlanillaRegistration,
} from "@/lib/planilla/planillaInscripcion";
import { downloadPlanillaXlsx } from "@/lib/planilla/planillaXlsx";
import { downloadPlanillaPdf } from "@/lib/pdf/planillaPdf";
import { useAuthStore } from "@/stores/authStore";
import type { Club, Event, EventRegistration } from "@/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
  club: Club | null | undefined;
  /** Inscripciones del club en este evento (todas; aquí se filtran). */
  registrations: EventRegistration[];
  /** Marca con uniforme a todos los aventureros que estén sin marcar. */
  onMarkAllUniform?: () => Promise<void> | void;
}

export default function PlanillaInscripcionModal({
  isOpen,
  onClose,
  event,
  club,
  registrations,
  onMarkAllUniform,
}: Props) {
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const [zone, setZone] = useState("");
  const [assistantPastor, setAssistantPastor] = useState("");
  const [minister, setMinister] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [marking, setMarking] = useState(false);
  const [generando, setGenerando] = useState<"xlsx" | "pdf" | null>(null);

  // Prefill con lo que quedó guardado la última vez.
  useEffect(() => {
    if (!isOpen) return;
    setZone(club?.zone ?? "");
    setAssistantPastor(club?.assistantPastor ?? "");
    setMinister(club?.minister ?? "");
    setContactPhone(club?.contactPhone ?? "");
  }, [
    isOpen,
    club?.zone,
    club?.assistantPastor,
    club?.minister,
    club?.contactPhone,
  ]);

  // La iglesia sólo se lee al abrir el modal: es un doc, pero esta página la
  // abren decenas de directores y las lecturas de más se pagan en el pico.
  const { data: church, isLoading: loadingChurch } = useQuery({
    queryKey: ["church", club?.churchId],
    queryFn: () =>
      club?.churchId
        ? churchService.getById(club.churchId)
        : Promise.resolve(null),
    enabled: isOpen && !!club?.churchId,
    staleTime: 5 * 60 * 1000,
  });

  /**
   * Las fichas se piden por id, SÓLO las de los inscritos y sólo al abrir.
   *
   * La página ya trae un `usePersons()`, pero ese filtra por asociación y por
   * `status == "active"`: una persona inactiva, o de otra asociación, salía
   * en la planilla sin documento, sin nacimiento y sin EPS, y nada en el
   * papel delataba que faltaba. Treinta lecturas puntuales cuestan menos que
   * una planilla mal impresa.
   */
  const personIds = useMemo(
    () =>
      Array.from(
        new Set(
          registrations
            .filter(isPlanillaRegistration)
            .map((r) => r.personId)
            .filter(Boolean),
        ),
      ).sort(),
    [registrations],
  );

  const { data: persons, isLoading: loadingPersons } = useQuery({
    queryKey: ["planilla-persons", personIds],
    queryFn: () => personService.getByIds(personIds),
    enabled: isOpen && personIds.length > 0,
    staleTime: 60 * 1000,
  });

  const personsById = useMemo(() => buildPersonsMap(persons), [persons]);

  const planilla = useMemo(
    () =>
      buildPlanilla({
        event,
        club,
        registrations,
        personsById,
        districtName: church?.districtName ?? "",
        overrides: { zone, assistantPastor, minister, contactPhone },
        generatedBy: {
          userId: user?.uid,
          displayName: profile?.displayName,
        },
      }),
    [
      event,
      club,
      registrations,
      personsById,
      church?.districtName,
      zone,
      assistantPastor,
      minister,
      contactPhone,
      user?.uid,
      profile?.displayName,
    ],
  );

  /** Aventureros vivos sin uniforme marcado: el aviso de antes de imprimir. */
  const sinUniforme = useMemo(
    () =>
      registrations.filter(
        (r) =>
          isPlanillaRegistration(r) &&
          isParticipantCategory(r.category) &&
          r.hasUniform !== true,
      ).length,
    [registrations],
  );

  const sinDatosPersona = useMemo(
    () =>
      loadingPersons
        ? 0
        : registrations.filter(
            (r) => isPlanillaRegistration(r) && !personsById.has(r.personId),
          ).length,
    [registrations, personsById, loadingPersons],
  );

  const guardarEncabezado = async () => {
    if (!club?.id) return;
    const patch: Partial<Club> = {};
    if ((club.zone ?? "") !== zone.trim()) patch.zone = zone.trim();
    if ((club.assistantPastor ?? "") !== assistantPastor.trim()) {
      patch.assistantPastor = assistantPastor.trim();
    }
    if ((club.minister ?? "") !== minister.trim()) {
      patch.minister = minister.trim();
    }
    if ((club.contactPhone ?? "") !== contactPhone.trim()) {
      patch.contactPhone = contactPhone.trim();
    }
    if (Object.keys(patch).length === 0) return;
    try {
      await clubService.update(club.id, {
        ...patch,
        updatedBy: user?.uid,
      } as Partial<Club>);
    } catch {
      // Que no se pueda recordar el encabezado no es razón para no entregar
      // la planilla: el director ya lo escribió y el archivo sale igual.
      toast.error("No pude guardar zona/pastor/ministro para la próxima vez");
    }
  };

  const generar = async (formato: "xlsx" | "pdf") => {
    if (planilla.totals.total === 0) {
      toast.error("No hay inscripciones activas para la planilla");
      return;
    }
    setGenerando(formato);
    try {
      await guardarEncabezado();
      // El Excel trae su propia librería por `import()` dinámico, así que la
      // primera vez tarda un segundo en bajarla: de ahí el estado de carga.
      if (formato === "xlsx") await downloadPlanillaXlsx(planilla);
      else downloadPlanillaPdf(planilla);
      toast.success(
        `Planilla en ${formato === "xlsx" ? "Excel" : "PDF"} descargada`,
      );
    } catch (e) {
      console.error("[Planilla] no se pudo generar", e);
      toast.error("No pude generar la planilla. Intenta de nuevo.");
    } finally {
      setGenerando(null);
    }
  };

  const marcarTodos = async () => {
    if (!onMarkAllUniform) return;
    setMarking(true);
    try {
      await onMarkAllUniform();
    } finally {
      setMarking(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Planilla general de inscripción"
      description={`${club?.name ?? "Tu club"} · ${event.name}`}
      size="lg"
    >
      <div className="space-y-5">
        {/* Encabezado que falta */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-1">
            Datos del encabezado
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            Lo que la planilla pide y la plataforma no tiene. Se queda
            grabado en tu club para la próxima vez.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              name="zone"
              label="Zona"
              value={zone}
              onChange={(e) => setZone(e.target.value)}
              placeholder="Ej. Zona Comunera"
            />
            <Input
              name="assistantPastor"
              label="Pastor asistente"
              value={assistantPastor}
              onChange={(e) => setAssistantPastor(e.target.value)}
              placeholder="Nombre del pastor"
            />
            <Input
              name="minister"
              label="Ministro"
              value={minister}
              onChange={(e) => setMinister(e.target.value)}
              placeholder="Nombre del ministro"
            />
            <Input
              name="contactPhone"
              label="Teléfono del club"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="Ej. 3001234567"
              inputMode="tel"
            />
          </div>
        </div>

        {/* Lo que la plataforma ya sabe */}
        <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3 text-xs text-gray-600 grid grid-cols-2 gap-x-4 gap-y-1">
          <span>
            <strong className="text-gray-900">Iglesia:</strong>{" "}
            {club?.churchName || "—"}
          </span>
          <span>
            <strong className="text-gray-900">Distrito:</strong>{" "}
            {loadingChurch ? "…" : planilla.header.districtName || "—"}
          </span>
          <span className="col-span-2">
            <strong className="text-gray-900">Director(a):</strong>{" "}
            {planilla.header.director || "—"}
            {/* Quien genera la planilla es quien firma: en un club con varias
                secciones, el director general y el de la sección no son la
                misma persona. */}
          </span>
        </div>

        {/* Aviso del uniforme */}
        {sinUniforme > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <div className="flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-medium">
                  {sinUniforme}{" "}
                  {sinUniforme === 1 ? "aventurero" : "aventureros"} sin
                  uniforme marcado
                </p>
                <p className="text-xs mt-0.5 text-amber-800">
                  Van a salir en la sección de “aventureros amigos (sin
                  uniforme)”. Si asisten uniformados, márcalos en la columna
                  <strong> Uniforme</strong> de Mis Inscritos.
                </p>
                {onMarkAllUniform && (
                  <button
                    type="button"
                    onClick={marcarTodos}
                    disabled={marking}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-amber-900 underline hover:no-underline disabled:opacity-50"
                  >
                    {marking && <Loader2 className="w-3 h-3 animate-spin" />}
                    Marcar los {sinUniforme} con uniforme
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {sinDatosPersona > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <strong>{sinDatosPersona}</strong>{" "}
            {sinDatosPersona === 1 ? "inscrito sale" : "inscritos salen"} sin
            documento ni fecha de nacimiento: no encontré su ficha. Revísalos
            en Miembros antes de imprimir.
          </div>
        )}

        {/* Resumen por sección */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2">
            Lo que va a salir
          </h4>
          {planilla.sections.length === 0 ? (
            <p className="text-sm text-gray-500">
              Todavía no hay inscripciones activas en este evento.
            </p>
          ) : (
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              {planilla.sections.map((s) => (
                <div
                  key={s.key}
                  className="flex items-center justify-between px-3 py-2 text-sm border-b last:border-b-0 bg-white"
                >
                  <span className="text-gray-700">
                    <span className="font-mono text-xs text-gray-400 mr-2">
                      {s.code}
                    </span>
                    {s.title}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {s.rows.length}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between px-3 py-2 text-sm bg-gray-50 font-semibold">
                <span>Total de inscritos</span>
                <span>{planilla.totals.total}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
        <Button
          variant="secondary"
          onClick={() => generar("xlsx")}
          disabled={planilla.totals.total === 0 || loadingPersons || !!generando}
          loading={generando === "xlsx"}
        >
          <FileSpreadsheet className="w-4 h-4 mr-1.5" />
          Excel
        </Button>
        <Button
          onClick={() => generar("pdf")}
          disabled={planilla.totals.total === 0 || loadingPersons || !!generando}
          loading={loadingPersons || generando === "pdf"}
        >
          <Download className="w-4 h-4 mr-1.5" />
          PDF
        </Button>
      </ModalFooter>
    </Modal>
  );
}
